import { useState } from "react";
import toast from "react-hot-toast";

import {
    useAdminConferencePackages,
    useCreateConferencePackage,
    useUpdateConferencePackage,
    useDeleteConferencePackage,
} from "@/services/adminConferencePackageService";
import type { ConferencePackage } from "@/services/adminConferencePackageService";
import { useAdminPeriods } from "@/services/adminPeriodService";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Edit2, Trash2, Loader2, Presentation } from "lucide-react";

const PARTICIPANT_TYPE_LABEL: Record<string, string> = {
    general: "Umum",
    student_s1_s2: "Mahasiswa S1/S2",
    student_s3: "Mahasiswa S3",
};
const TIER_LABEL: Record<string, string> = {
    early_bird: "Early Bird",
    regular: "Regular",
};

const toInputDateTime = (value: string | null | undefined) => {
    if (!value) return "";
    return value.substring(0, 16).replace(" ", "T");
};

const emptyForm = {
    period_id: "",
    service_id: "",
    code: "",
    name: "",
    description: "",
    is_presenter: false,
    participant_type_target: "general" as ConferencePackage["participant_type_target"],
    tier_name: "early_bird" as ConferencePackage["tier_name"],
    start_date: "",
    end_date: "",
    currency: "IDR" as ConferencePackage["currency"],
    price: "0",
    is_active: true,
};

export default function ConferencePackagesTab() {
    const { data: periods } = useAdminPeriods();
    const [periodFilter, setPeriodFilter] = useState<string>("all");

    const { data: packages = [], isLoading } = useAdminConferencePackages(periodFilter !== "all" ? periodFilter : undefined);
    const createMutation = useCreateConferencePackage();
    const updateMutation = useUpdateConferencePackage();
    const deleteMutation = useDeleteConferencePackage();

    const [isOpen, setIsOpen] = useState(false);
    const [editing, setEditing] = useState<ConferencePackage | null>(null);
    const [form, setForm] = useState(emptyForm);

    const [isAlertOpen, setIsAlertOpen] = useState(false);
    const [pendingDeleteId, setPendingDeleteId] = useState<number | null>(null);

    const openCreate = () => {
        setEditing(null);
        setForm({ ...emptyForm, period_id: periodFilter !== "all" ? periodFilter : (periods?.[0]?.period_id.toString() || "") });
        setIsOpen(true);
    };

    const openEdit = (pkg: ConferencePackage) => {
        setEditing(pkg);
        setForm({
            period_id: String(pkg.period_id),
            service_id: String(pkg.service_id),
            code: pkg.code || "",
            name: pkg.name,
            description: pkg.description || "",
            is_presenter: Boolean(pkg.is_presenter),
            participant_type_target: pkg.participant_type_target,
            tier_name: pkg.tier_name,
            start_date: toInputDateTime(pkg.start_date),
            end_date: toInputDateTime(pkg.end_date),
            currency: pkg.currency,
            price: String(pkg.price),
            is_active: Boolean(pkg.is_active),
        });
        setIsOpen(true);
    };

    const handleSubmit = () => {
        if (!form.period_id || !form.service_id || !form.name.trim() || !form.start_date || !form.end_date) {
            toast.error("Lengkapi field wajib terlebih dahulu");
            return;
        }
        const payload = {
            period_id: Number(form.period_id),
            service_id: Number(form.service_id),
            code: form.code || null,
            name: form.name.trim(),
            description: form.description || null,
            is_presenter: form.is_presenter,
            participant_type_target: form.participant_type_target,
            tier_name: form.tier_name,
            start_date: form.start_date.replace("T", " ") + ":00",
            end_date: form.end_date.replace("T", " ") + ":00",
            currency: form.currency,
            price: Number(form.price),
            is_active: form.is_active,
        };

        if (editing) {
            updateMutation.mutate({ id: editing.package_id, payload }, {
                onSuccess: () => { toast.success("Paket conference diperbarui"); setIsOpen(false); },
                onError: (e: any) => toast.error(e?.response?.data?.message || "Gagal memperbarui paket"),
            });
        } else {
            createMutation.mutate(payload, {
                onSuccess: () => { toast.success("Paket conference ditambahkan"); setIsOpen(false); },
                onError: (e: any) => toast.error(e?.response?.data?.message || "Gagal menambahkan paket"),
            });
        }
    };

    const handleDelete = (id: number) => {
        setPendingDeleteId(id);
        setIsAlertOpen(true);
    };

    const confirmDelete = () => {
        if (!pendingDeleteId) return;
        deleteMutation.mutate(pendingDeleteId, {
            onSuccess: () => toast.success("Paket conference dihapus"),
            onError: (e: any) => toast.error(e?.response?.data?.message || "Gagal menghapus paket"),
        });
        setIsAlertOpen(false);
    };

    const formatCurrency = (amount: string | number, currency: string) => {
        return new Intl.NumberFormat("id-ID", { style: "currency", currency, minimumFractionDigits: 0 }).format(Number(amount));
    };

    const isSaving = createMutation.isPending || updateMutation.isPending;

    return (
        <div className="space-y-4">
            <p className="text-sm text-slate-500 dark:text-slate-400">
                Paket harga pendaftaran Conference (submission OJS) — dibedakan berdasarkan presenter/non-presenter,
                tipe peserta, dan gelombang harga (early bird/regular). Data ini terpisah dari Paket Konvensi.
            </p>

            <div className="flex flex-wrap items-center justify-between gap-3">
                <Select value={periodFilter} onValueChange={setPeriodFilter}>
                    <SelectTrigger className="h-10 rounded-xl w-[220px]"><SelectValue placeholder="Semua Periode" /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">Semua Periode</SelectItem>
                        {periods?.map((p) => (
                            <SelectItem key={p.period_id} value={String(p.period_id)}>{p.name}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <Button onClick={openCreate} className="h-10 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-bold">
                    <Plus className="w-4 h-4 mr-1.5" /> Tambah Paket Conference
                </Button>
            </div>

            <div className="border border-slate-200 dark:border-slate-800 rounded-2xl overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm whitespace-nowrap">
                        <thead className="bg-slate-50 dark:bg-slate-800/50 text-slate-500 dark:text-slate-400 font-bold uppercase tracking-wider text-[11px]">
                            <tr>
                                <th className="px-4 py-3">Nama Paket</th>
                                <th className="px-4 py-3 text-center">Presenter?</th>
                                <th className="px-4 py-3">Tipe Peserta</th>
                                <th className="px-4 py-3">Gelombang</th>
                                <th className="px-4 py-3">Periode Berlaku</th>
                                <th className="px-4 py-3">Harga</th>
                                <th className="px-4 py-3 text-center">Status</th>
                                <th className="px-4 py-3 text-right">Aksi</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                            {isLoading ? (
                                <tr><td colSpan={8} className="p-8 text-center"><Loader2 className="w-5 h-5 animate-spin inline text-indigo-500" /></td></tr>
                            ) : packages.length === 0 ? (
                                <tr><td colSpan={8} className="p-8 text-center text-slate-400">Belum ada paket conference.</td></tr>
                            ) : packages.map((pkg) => (
                                <tr key={pkg.package_id} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/20">
                                    <td className="px-4 py-3 font-semibold text-indigo-600 dark:text-indigo-400 max-w-[220px]">
                                        <div className="truncate" title={pkg.name}>{pkg.name}</div>
                                        {pkg.code && <div className="text-[11px] text-slate-400">{pkg.code}</div>}
                                    </td>
                                    <td className="px-4 py-3 text-center">
                                        {pkg.is_presenter ? (
                                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                                                <Presentation className="w-3 h-3" /> Ya
                                            </span>
                                        ) : "-"}
                                    </td>
                                    <td className="px-4 py-3 text-slate-500">{PARTICIPANT_TYPE_LABEL[pkg.participant_type_target]}</td>
                                    <td className="px-4 py-3">
                                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${pkg.tier_name === "early_bird" ? "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400" : "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300"}`}>
                                            {TIER_LABEL[pkg.tier_name]}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 text-slate-500 text-xs">
                                        {new Date(pkg.start_date).toLocaleDateString("id-ID")} - {new Date(pkg.end_date).toLocaleDateString("id-ID")}
                                    </td>
                                    <td className="px-4 py-3 font-medium">{formatCurrency(pkg.price, pkg.currency)}</td>
                                    <td className="px-4 py-3 text-center">
                                        {pkg.is_active ? (
                                            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">Aktif</span>
                                        ) : (
                                            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400">Nonaktif</span>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                        <div className="flex justify-end gap-1.5">
                                            <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => openEdit(pkg)}><Edit2 className="w-3.5 h-3.5" /></Button>
                                            <Button variant="outline" size="icon" className="h-8 w-8 text-rose-600" onClick={() => handleDelete(pkg.package_id)}><Trash2 className="w-3.5 h-3.5" /></Button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <Dialog open={isOpen} onOpenChange={setIsOpen}>
                <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
                    <DialogHeader><DialogTitle>{editing ? "Edit Paket Conference" : "Tambah Paket Conference"}</DialogTitle></DialogHeader>
                    <div className="space-y-3 pt-2">
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="text-xs font-bold text-slate-500 block mb-1">Periode</label>
                                <Select value={form.period_id} onValueChange={(v) => setForm((f) => ({ ...f, period_id: v }))}>
                                    <SelectTrigger><SelectValue placeholder="Pilih Periode" /></SelectTrigger>
                                    <SelectContent>
                                        {periods?.map((p) => (
                                            <SelectItem key={p.period_id} value={String(p.period_id)}>{p.name}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <label className="text-xs font-bold text-slate-500 block mb-1">Service ID (referensi gateway pembayaran)</label>
                                <Input type="number" value={form.service_id} onChange={(e) => setForm((f) => ({ ...f, service_id: e.target.value }))} placeholder="mis. 953" />
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="text-xs font-bold text-slate-500 block mb-1">Kode (Opsional)</label>
                                <Input value={form.code} onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))} />
                            </div>
                            <div>
                                <label className="text-xs font-bold text-slate-500 block mb-1">Nama Paket</label>
                                <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="mis. EB - Presenter Author (General) IDR" />
                            </div>
                        </div>

                        <div>
                            <label className="text-xs font-bold text-slate-500 block mb-1">Deskripsi (Opsional)</label>
                            <Textarea className="resize-none" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} />
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="text-xs font-bold text-slate-500 block mb-1">Tipe Peserta</label>
                                <Select value={form.participant_type_target} onValueChange={(v) => setForm((f) => ({ ...f, participant_type_target: v as any }))}>
                                    <SelectTrigger><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="general">Umum</SelectItem>
                                        <SelectItem value="student_s1_s2">Mahasiswa S1/S2</SelectItem>
                                        <SelectItem value="student_s3">Mahasiswa S3</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <label className="text-xs font-bold text-slate-500 block mb-1">Gelombang Harga</label>
                                <Select value={form.tier_name} onValueChange={(v) => setForm((f) => ({ ...f, tier_name: v as any }))}>
                                    <SelectTrigger><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="early_bird">Early Bird</SelectItem>
                                        <SelectItem value="regular">Regular</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="text-xs font-bold text-slate-500 block mb-1">Berlaku Mulai</label>
                                <Input type="datetime-local" value={form.start_date} onChange={(e) => setForm((f) => ({ ...f, start_date: e.target.value }))} />
                            </div>
                            <div>
                                <label className="text-xs font-bold text-slate-500 block mb-1">Berlaku Sampai</label>
                                <Input type="datetime-local" value={form.end_date} onChange={(e) => setForm((f) => ({ ...f, end_date: e.target.value }))} />
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="text-xs font-bold text-slate-500 block mb-1">Mata Uang</label>
                                <Select value={form.currency} onValueChange={(v) => setForm((f) => ({ ...f, currency: v as any }))}>
                                    <SelectTrigger><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="IDR">IDR</SelectItem>
                                        <SelectItem value="USD">USD</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <label className="text-xs font-bold text-slate-500 block mb-1">Harga</label>
                                <Input type="number" min="0" value={form.price} onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))} />
                            </div>
                        </div>

                        <div className="flex items-center gap-6 pt-1">
                            <label className="flex items-center gap-2 text-sm">
                                <Switch checked={form.is_presenter} onCheckedChange={(c) => setForm((f) => ({ ...f, is_presenter: c }))} />
                                Untuk Presenter
                            </label>
                            <label className="flex items-center gap-2 text-sm">
                                <Switch checked={form.is_active} onCheckedChange={(c) => setForm((f) => ({ ...f, is_active: c }))} />
                                Aktif
                            </label>
                        </div>

                        <div className="flex justify-end gap-2 pt-2">
                            <Button variant="outline" onClick={() => setIsOpen(false)}>Batal</Button>
                            <Button onClick={handleSubmit} disabled={isSaving} className="bg-indigo-600 hover:bg-indigo-700 text-white">
                                {isSaving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Simpan
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            <AlertDialog open={isAlertOpen} onOpenChange={setIsAlertOpen}>
                <AlertDialogContent className="rounded-3xl max-w-sm">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Hapus Paket Conference</AlertDialogTitle>
                        <AlertDialogDescription>
                            Yakin hapus paket ini? Jika masih dipakai perhitungan tagihan submission yang sudah ada, hal ini bisa memengaruhi data historis.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Batal</AlertDialogCancel>
                        <AlertDialogAction className="bg-rose-600 hover:bg-rose-700" onClick={confirmDelete}>Hapus</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
